import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { readWeatherState } from '../state/hud-weather.js';
import { useSessionStore } from '../state/session-store.js';
import {
  currentWelcomeWeather,
  formatWelcomeDate,
  parseWeatherOverride,
  SPLASH_FADE_MS,
  SPLASH_VISIBLE_MS,
  splashPhaseAt,
  weatherIcon,
  type WeatherReading,
} from '../state/welcome-weather.js';
import { WeatherIcon } from './WeatherIcon.js';

/**
 * Greets the player on entering the office with today's date and the weather outside.
 * Mounted with the office view, so it shows on every entry but not on map swaps or reconnects.
 * Taking the lift to the other office shows it again for that office (ADR-0037).
 * It never blocks input, and nothing dismisses it early: it always shows for 5 s, then fades.
 */
export function WelcomeSplash() {
  const { t, i18n } = useTranslation();
  const name = useSessionStore((s) => s.user?.displayName);
  const room = useSessionStore((s) => s.room);
  const region = useSessionStore((s) => s.region);
  const [phase, setPhase] = useState<'shown' | 'fading' | 'gone'>('shown');
  const [now] = useState(() => new Date());
  const [override] = useState(() =>
    import.meta.env.DEV ? parseWeatherOverride(window.location.search) : {},
  );
  const [reading, setReading] = useState<WeatherReading | undefined>(() =>
    readWeatherState(room?.state, region),
  );

  // The weather arrives with the first room state patch, possibly just after the splash appears.
  useEffect(() => {
    if (!room) return;
    setReading(readWeatherState(room.state, region));
    const onState = (state: unknown) => setReading(readWeatherState(state, region));
    room.onStateChange(onState);
    return () => room.onStateChange.remove(onState);
  }, [room, region]);

  const weather = currentWelcomeWeather(now, override, reading);

  // Always 5 s, then the fade; input never shortens it (spec §6.11).
  useEffect(() => {
    const fade = window.setTimeout(
      () => setPhase(splashPhaseAt(SPLASH_VISIBLE_MS)),
      SPLASH_VISIBLE_MS,
    );
    const remove = window.setTimeout(
      () => setPhase(splashPhaseAt(SPLASH_VISIBLE_MS + SPLASH_FADE_MS)),
      SPLASH_VISIBLE_MS + SPLASH_FADE_MS,
    );
    return () => {
      window.clearTimeout(fade);
      window.clearTimeout(remove);
    };
  }, []);

  if (phase === 'gone') return null;

  const known = weather.condition !== 'unknown';
  const place = region ?? t('welcome.place');
  const details = [
    known ? t(`welcome.condition.${weather.condition}`) : undefined,
    weather.temperatureC !== undefined
      ? t('welcome.temperature', { value: Math.round(weather.temperatureC) })
      : undefined,
  ].filter(Boolean);

  return (
    <div className={`welcome-splash${phase === 'fading' ? ' fading' : ''}`} role="status">
      <div className="welcome-card">
        <WeatherIcon kind={weatherIcon(weather)} />
        <p className="welcome-title">
          {name ? t('welcome.titleNamed', { name }) : t('welcome.title')}
        </p>
        <p className="welcome-date">{formatWelcomeDate(now, i18n.language)}</p>
        <p className="welcome-weather">
          {details.length > 0
            ? t('welcome.weatherIn', { place, details: details.join(', ') })
            : place}
        </p>
      </div>
    </div>
  );
}
